/**
 * CompletionPredictionCard Component
 * Displays completion prediction with confidence levels
 *
 * Story 4.4: Completion Prediction
 */

import React from 'react';
import { clsx } from 'clsx';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Card } from '../../common';
import { OrgBaselineNote } from '../ReportScopeIndicator';
import type { CompletionPredictionData, ConfidenceLevel, ImpactType } from '../../../services/reportApi';

export interface CompletionPredictionCardProps {
  /** Prediction data */
  data: CompletionPredictionData;
  /** Loading state */
  isLoading?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Get confidence styling
 */
function getConfidenceStyle(level: ConfidenceLevel): { color: string; bg: string; label: string } {
  switch (level) {
    case 'high':
      return { color: 'text-green-700', bg: 'bg-green-100', label: 'High Confidence' };
    case 'medium':
      return { color: 'text-amber-700', bg: 'bg-amber-100', label: 'Medium Confidence' };
    case 'low':
      return { color: 'text-red-700', bg: 'bg-red-100', label: 'Low Confidence' };
  }
}

/**
 * Get impact styling
 */
function getImpactStyle(impact: ImpactType): { icon: React.ComponentType<{ className?: string }>; color: string } {
  switch (impact) {
    case 'positive':
      return { icon: ArrowUp, color: 'text-green-600' };
    case 'negative':
      return { icon: ArrowDown, color: 'text-red-600' };
    case 'neutral':
      return { icon: Minus, color: 'text-stone-500' };
  }
}

/**
 * Loading skeleton
 */
const PredictionSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-center mb-6">
      <div className="w-40 h-40 rounded-full bg-stone-200" />
    </div>
    <div className="grid grid-cols-3 gap-4 mb-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="text-center">
          <div className="h-8 bg-stone-200 rounded w-16 mx-auto mb-2" />
          <div className="h-4 bg-stone-200 rounded w-20 mx-auto" />
        </div>
      ))}
    </div>
    <div className="space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-12 bg-stone-200 rounded" />
      ))}
    </div>
  </div>
);

/**
 * Circular gauge component
 */
const PredictionGauge: React.FC<{
  prediction: number;
  confidence: ConfidenceLevel;
  range: { min: number; max: number };
}> = ({ prediction, confidence, range }) => {
  const confidenceStyle = getConfidenceStyle(confidence);

  // Calculate stroke dash for the prediction arc
  const radius = 70;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = `${(prediction / 100) * circumference} ${circumference}`;

  return (
    <div className="relative w-44 h-44 mx-auto">
      {/* Background circle */}
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke="#e7e5e4"
          strokeWidth="14"
        />
        {/* Confidence range arc (lighter) */}
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke="#d6d3d1"
          strokeWidth="14"
          strokeDasharray={`${(range.max / 100) * circumference} ${circumference}`}
          strokeLinecap="round"
        />
        {/* Min range arc */}
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke="#a8a29e"
          strokeWidth="14"
          strokeDasharray={`${(range.min / 100) * circumference} ${circumference}`}
          strokeLinecap="round"
        />
        {/* Prediction arc */}
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke={prediction >= 70 ? '#22c55e' : prediction >= 50 ? '#f59e0b' : '#ef4444'}
          strokeWidth="14"
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
        />
      </svg>
      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold text-stone-900">{prediction}%</span>
        <span className={clsx(
          'mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
          confidenceStyle.bg,
          confidenceStyle.color
        )}>
          {confidenceStyle.label}
        </span>
      </div>
    </div>
  );
};

/**
 * Completion Prediction Card Component
 */
export const CompletionPredictionCard: React.FC<CompletionPredictionCardProps> = ({
  data,
  isLoading = false,
  className,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Completion Prediction</h3>
        <PredictionSkeleton />
      </Card>
    );
  }

  const {
    predictedCompletionRate,
    confidenceLevel,
    confidenceRange,
    currentCompletionRate,
    projectedByStatus,
    weekOverWeekTrend,
    factors,
  } = data;

  const TrendIcon = weekOverWeekTrend > 0 ? TrendingUp : weekOverWeekTrend < 0 ? TrendingDown : Minus;
  const trendColor = weekOverWeekTrend > 0 ? 'text-green-600' : weekOverWeekTrend < 0 ? 'text-red-600' : 'text-stone-500';

  return (
    <Card className={clsx('p-6', className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-stone-900">Completion Prediction</h3>
        <div className={clsx('flex items-center gap-1', trendColor)}>
          <TrendIcon className="w-4 h-4" />
          <span className="text-sm font-medium">
            {weekOverWeekTrend > 0 ? '+' : ''}{weekOverWeekTrend.toFixed(1)}% WoW
          </span>
        </div>
      </div>

      {/* Prediction gauge */}
      <PredictionGauge
        prediction={predictedCompletionRate}
        confidence={confidenceLevel}
        range={confidenceRange}
      />

      {/* Range indicator */}
      <p className="text-center text-sm text-stone-500 mt-2">
        Predicted range: {confidenceRange.min}% - {confidenceRange.max}%
      </p>

      {/* Status breakdown */}
      <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-stone-200">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
            <CheckCircle className="w-4 h-4" />
            <span className="text-xl font-bold">{projectedByStatus.willComplete}</span>
          </div>
          <p className="text-xs text-stone-500">Will Complete</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-xl font-bold">{projectedByStatus.atRisk}</span>
          </div>
          <p className="text-xs text-stone-500">At Risk</p>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-red-600 mb-1">
            <XCircle className="w-4 h-4" />
            <span className="text-xl font-bold">{projectedByStatus.unlikely}</span>
          </div>
          <p className="text-xs text-stone-500">Unlikely</p>
        </div>
      </div>

      {/* Current vs predicted */}
      <div className="mt-4 p-3 bg-stone-50 rounded-lg">
        <div className="flex items-center justify-between text-sm">
          <span className="text-stone-600">Current completion rate</span>
          <span className="font-medium text-stone-900">{currentCompletionRate}%</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-1">
          <span className="text-stone-600">Predicted end-of-period</span>
          <span className="font-medium text-stone-900">{predictedCompletionRate}%</span>
        </div>
        {/* Organization comparison for a scoped caller (absent for an admin,
            for whom the figures above already ARE the organization). */}
        {data.orgBaseline && (
          <OrgBaselineNote
            className="mt-2"
            value={`${data.orgBaseline.predictedCompletionRate}%`}
            label="predicted end-of-period"
          />
        )}
      </div>

      {/* Key factors */}
      {factors.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-stone-700 mb-2">Key Factors</h4>
          <div className="space-y-2">
            {factors.slice(0, 4).map((factor, index) => {
              const impactStyle = getImpactStyle(factor.impact);
              const ImpactIcon = impactStyle.icon;
              return (
                <div key={index} className="flex items-start gap-2 text-sm">
                  <ImpactIcon className={clsx('w-4 h-4 shrink-0 mt-0.5', impactStyle.color)} />
                  <div>
                    <span className="font-medium text-stone-700">{factor.factor}: </span>
                    <span className="text-stone-600">{factor.description}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

export default CompletionPredictionCard;
